import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper,
    Grid,
    Typography,
    Button,
    CircularProgress,
    Box,
    Divider,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import TableChartIcon from '@material-ui/icons/TableChart';
import GetAppIcon from '@material-ui/icons/GetApp'; // Import for Export Icon
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { differenceInCalendarDays, addDays } from 'date-fns';
// Models for Sales by Root Commodity Group Report defined locally
export interface ContributingCommodityGroup {
    name: string;
    gross_item_price: number;
    quantity_sold: number;
}

export interface RootCommodityGroupSalesData {
    root_commodity_group_uuid: string;
    root_commodity_group_name: string;
    root_commodity_group_number: string;
    total_gross_item_price: number;
    total_quantity_sold: number;
    contributing_commodity_groups: ContributingCommodityGroup[];
}

export interface SalesByRootCommodityGroupResponseItem {
    business_id: number;
    business_name: string;
    current_period_data: RootCommodityGroupSalesData[];
    previous_period_data: RootCommodityGroupSalesData[];
}

export type SalesByRootCommodityGroupAPIResponse = SalesByRootCommodityGroupResponseItem[];

// This type will be used internally to store the processed data for current/previous periods separately
// as the chart logic already expects this separation.
export type ProcessedSalesByRootCommodityGroupResponse = Array<{
    business_id: number;
    business_name: string;
    data: RootCommodityGroupSalesData[];
}>;

import { getSalesByRootCommodityGroup } from '../../api';
import DateShortcutDropdown, { DEFAULT_SHORTCUT_OPTIONS } from '../../components/DateShortcutDropdown';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData
} from 'chart.js';
import { connect } from 'react-redux';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    title: {
        marginBottom: theme.spacing(3),
    },
    formControl: {
        marginBottom: theme.spacing(2),
        minWidth: 120,
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    chartContainer: {
        marginTop: theme.spacing(4),
        height: '70vh', // Responsive height
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 400,
    },
    comparisonButtonRow: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    comparisonButton: {
        marginRight: theme.spacing(1),
        // marginBottom: theme.spacing(1), // Removed for better vertical alignment
    },
    filterButtonContainer: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between', // Changed from flex-start
        alignItems: 'center', // Added for vertical alignment
    },
    legendContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap', // Keep columns in a single line
        marginTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        overflowX: 'auto', // Allow horizontal scrolling
    },
    legendColumn: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: theme.spacing(3), // Space between columns
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    legendColorBox: {
        width: 24, // Increased width
        height: 12,
        marginRight: theme.spacing(1),
        border: '1px solid #ccc',
    },
    legendLabel: {
        fontSize: '0.75rem', // Decreased font size
    },
    dateDisplayContainer: {
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
    periodDateContainer: { // New style for the period date display
        marginTop: theme.spacing(2),
    },
}));

// Date helper functions (copied for now, consider moving to a shared util if used in more places)
const getStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const getEndOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getStartOfThisMonthHelper = (): Date => { // Renamed to avoid conflict with component's getStartOfMonth
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
};
const getEndOfThisMonthHelper = (): Date => { // Renamed
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};
const getStartOfLastMonthHelper = (): Date => { // Renamed
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1);
};
const getEndOfLastMonthHelper = (): Date => { // Renamed
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59, 999);
};
const getStartOfThisQuarterHelper = (): Date => { // Renamed
    const date = new Date();
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
};
const getEndOfThisQuarterHelper = (): Date => { // Renamed
    const date = new Date();
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999);
};
const getStartOfLastQuarterHelper = (): Date => { // Renamed
    const date = new Date();
    const currentQuarter = Math.floor(date.getMonth() / 3);
    const firstMonthOfCurrentQuarter = currentQuarter * 3;
    return new Date(date.getFullYear(), firstMonthOfCurrentQuarter - 3, 1);
};
const getEndOfLastQuarterHelper = (): Date => { // Renamed
    const date = new Date();
    const currentQuarter = Math.floor(date.getMonth() / 3);
    const firstMonthOfCurrentQuarter = currentQuarter * 3;
    return new Date(date.getFullYear(), firstMonthOfCurrentQuarter, 0, 23, 59, 59, 999);
};
const getStartOfThisYearHelper = (): Date => new Date(new Date().getFullYear(), 0, 1); // Renamed
const getEndOfThisYearHelper = (): Date => new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999); // Renamed
const getStartOfLastYearHelper = (): Date => new Date(new Date().getFullYear() - 1, 0, 1); // Renamed
const getEndOfLastYearHelper = (): Date => new Date(new Date().getFullYear() - 1, 11, 31, 23, 59, 59, 999); // Renamed

const getLastYearSameDay = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), ref.getDate());
const getLastYearSameMonthStart = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), 1);
const getLastYearSameMonthEnd = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth() + 1, 0, 23, 59, 59, 999);
const getLastYearSameQuarterStart = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3, 1);
};
const getLastYearSameQuarterEnd = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3 + 3, 0, 23, 59, 59, 999);
};

// Helper to generate distinct colors
const generateColor = (index: number): string => {
    const colors = [
        'rgba(114, 147, 203, 0.7)', // Muted Blue
        'rgba(255, 151, 76, 0.7)',  // Orange
        'rgba(132, 186, 91, 0.7)',  // Green
        'rgba(211, 94, 96, 0.7)',   // Red
        'rgba(128, 133, 133, 0.7)', // Grey
        'rgba(144, 103, 167, 0.7)', // Purple
        'rgba(171, 104, 87, 0.7)',  // Brown
        'rgba(204, 194, 16, 0.7)',  // Yellow-Green
        'rgba(0, 152, 160, 0.7)',   // Teal
        'rgba(230, 130, 190, 0.7)', // Pink
        'rgba(60, 179, 113, 0.7)',  // Medium Sea Green
        'rgba(255, 105, 180, 0.7)', // Hot Pink (Muted)
        'rgba(70, 130, 180, 0.7)',  // Steel Blue
        'rgba(218, 165, 32, 0.7)',  // Goldenrod
        'rgba(95, 158, 160, 0.7)',  // Cadet Blue
        'rgba(255, 140, 0, 0.7)',   // Dark Orange
        'rgba(154, 205, 50, 0.7)',  // Yellow Green (Slightly different)
        'rgba(72, 61, 139, 0.7)',   // Dark Slate Blue
        'rgba(210, 105, 30, 0.7)',  // Chocolate
        'rgba(0, 206, 209, 0.7)'   // Dark Turquoise
    ];
    return colors[index % colors.length];
};

const MAYURI_BUSINESSES = [6, 34, 69, 172, 175];
const CAN_VIEW_SALES_BY_COMMODITY_GROUP_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS = 'view_sales_by_commodity_group_report_for_all_related_business_locations';


const SalesByRootCommodityGroupScreen: React.FC = (props: any) => {
    const classes = useStyles();

    const getStartOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);
    const getEndOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const today = new Date();
    const firstDayCurrentMonth = getStartOfMonth(today); // This component's local getStartOfMonth
    let initialCurrentPeriodEnd = getEndOfMonth(today); // This component's local getEndOfMonth
    if (initialCurrentPeriodEnd > getEndOfDay(today)) {
        initialCurrentPeriodEnd = getEndOfDay(today);
    }

    const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const firstDayPrevMonth = getStartOfMonth(prevMonthDate);
    const lastDayPrevMonth = getEndOfMonth(prevMonthDate);

    const [currentPeriodStart, setCurrentPeriodStart] = useState<Date | null>(firstDayCurrentMonth);
    const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(initialCurrentPeriodEnd);
    const [prevPeriodStart, setPrevPeriodStart] = useState<Date | null>(firstDayPrevMonth);
    // actualPrevPeriodEnd is the one used for API calls and display
    const [actualPrevPeriodEnd, setActualPrevPeriodEnd] = useState<Date | null>(lastDayPrevMonth);
    // fullPrevPeriodEndFromShortcut stores the P2 end date as determined by a shortcut, before limiting
    const [fullPrevPeriodEndFromShortcut, setFullPrevPeriodEndFromShortcut] = useState<Date | null>(lastDayPrevMonth);

    // State for DateShortcutDropdown selected keys
    const [currentPeriodShortcut, setCurrentPeriodShortcut] = useState<string>('this_month');
    const [previousPeriodShortcut, setPreviousPeriodShortcut] = useState<string>('last_month');
    const [limitPeriodRange, setLimitPeriodRange] = useState<boolean>(false);
    const [showLimitPeriodRangeCheckbox, setShowLimitPeriodRangeCheckbox] = useState<boolean>(true);

    const [comparisonAnchorEl, setComparisonAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedComparisonLabel, setSelectedComparisonLabel] = useState<string>('Comparison Shortcuts');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false); // New state for filter visibility
    const [businessIds, setBusinessIds] = useState<number[]>([]);
    const [showAllMayuriLocations, setShowAllMayuriLocations] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    // These will now store the processed data for each period
    const [currentPeriodReportData, setCurrentPeriodReportData] = useState<ProcessedSalesByRootCommodityGroupResponse | null>(null);
    const [prevPeriodReportData, setPrevPeriodReportData] = useState<ProcessedSalesByRootCommodityGroupResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showLegend, setShowLegend] = useState<boolean>(false);
    const [showValuesTable, setShowValuesTable] = useState<boolean>(false);
    const chartRef = React.useRef<ChartJS<'bar', any[], string> | null>(null);
    const [exportingCsv, setExportingCsv] = useState<boolean>(false);

    const allRootGroupNamesSorted = useMemo(() => {
        if (!currentPeriodReportData || !prevPeriodReportData) return [];
        return Array.from(
            new Set([
                ...currentPeriodReportData.flatMap(item => item.data.map(rg => rg.root_commodity_group_name)),
                ...prevPeriodReportData.flatMap(item => item.data.map(rg => rg.root_commodity_group_name))
            ])
        ).sort();
    }, [currentPeriodReportData, prevPeriodReportData]);

    const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateForDisplay = (date: Date | null): string => {
        if (!date) return 'N/A';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Function to format currency (copied from BusinessYearlyComparison)
    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(value);
    };

    const fetchData = async () => {
        if (businessIds.length === 0) {
            setError("Please select at least one business.");
            setLoading(false);
            return;
        }
        if (!currentPeriodStart || !currentPeriodEnd || !prevPeriodStart || !actualPrevPeriodEnd) {
            setError("Please ensure all date periods are selected.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        const businessIdsString = businessIds.join(',');

        setCurrentPeriodReportData(null);
        setPrevPeriodReportData(null);

        try {
            const response = await getSalesByRootCommodityGroup(
                formatDateForAPI(currentPeriodStart),
                formatDateForAPI(currentPeriodEnd),
                formatDateForAPI(prevPeriodStart),
                formatDateForAPI(actualPrevPeriodEnd), // Use actualPrevPeriodEnd
                businessIdsString
            );

            if (response) {
                const jsonData: SalesByRootCommodityGroupAPIResponse = await response.json();

                // Process the combined response into separate arrays for current and previous periods
                const processedCurrentData: ProcessedSalesByRootCommodityGroupResponse = jsonData.map(item => ({
                    business_id: item.business_id,
                    business_name: item.business_name,
                    data: item.current_period_data || [], // Ensure data is an array
                }));
                const processedPreviousData: ProcessedSalesByRootCommodityGroupResponse = jsonData.map(item => ({
                    business_id: item.business_id,
                    business_name: item.business_name,
                    data: item.previous_period_data || [], // Ensure data is an array
                }));

                setCurrentPeriodReportData(processedCurrentData);
                setPrevPeriodReportData(processedPreviousData);
            }
        } catch (err) {
            console.error('Error fetching sales by root commodity group data:', err);
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleExportCsv = async () => {
        if (businessIds.length === 0) {
            setError("Please select at least one business to export.");
            return;
        }
        if (!currentPeriodStart || !currentPeriodEnd || !prevPeriodStart || !actualPrevPeriodEnd) { // Check actualPrevPeriodEnd
            setError("Please ensure all date periods are selected to export.");
            return;
        }

        setExportingCsv(true);
        setError(null);
        const businessIdsString = businessIds.join(',');

        try {
            // Assuming getSalesByRootCommodityGroup is updated to accept a 6th boolean param for export_csv
            const response = await getSalesByRootCommodityGroup(
                formatDateForAPI(currentPeriodStart),
                formatDateForAPI(currentPeriodEnd),
                formatDateForAPI(prevPeriodStart),
                formatDateForAPI(actualPrevPeriodEnd), // Use actualPrevPeriodEnd
                businessIdsString,
                true // Request CSV export
            );

            if (response && response.ok) {
                const blob = await response.blob();
                const disposition = response.headers.get('content-disposition');
                let filename = `sales_by_root_commodity_group_export_${new Date().toISOString().split('T')[0]}.csv`; // Default filename
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                    }
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else {
                const errorText = response ? await response.text() : 'No response';
                console.error('Failed to export CSV:', errorText);
                setError(`Failed to export CSV. Server said: ${response?.statusText || errorText}`);
            }
        } catch (err: any) {
            console.error('Error exporting CSV:', err);
            setError(`An error occurred while exporting CSV: ${err.message || 'Unknown error'}`);
        } finally {
            setExportingCsv(false);
        }
    };

    const applyComparisonShortcut = async (type: string) => {
        let newCurrentStart: Date | null = null;
        let newCurrentEnd: Date | null = null;
        let newPreviousStart: Date | null = null;
        let newPreviousEnd: Date | null = null;
        const todayDate = new Date();

        const isQuarterShortcut = type.includes('quarter');
        setShowLimitPeriodRangeCheckbox(!isQuarterShortcut);
        if (isQuarterShortcut) {
            setLimitPeriodRange(false);
        }

        switch (type) {
            case 'today_vs_yesterday':
                newCurrentStart = getStartOfDay(todayDate);
                newCurrentEnd = getEndOfDay(todayDate);
                const yesterday = new Date(todayDate);
                yesterday.setDate(todayDate.getDate() - 1);
                newPreviousStart = getStartOfDay(yesterday);
                newPreviousEnd = getEndOfDay(yesterday);
                setCurrentPeriodShortcut('today');
                setPreviousPeriodShortcut('yesterday');
                break;
            case 'today_vs_last_year_today':
                newCurrentStart = getStartOfDay(todayDate);
                newCurrentEnd = getEndOfDay(todayDate);
                const lastYearToday = getLastYearSameDay(todayDate);
                newPreviousStart = getStartOfDay(lastYearToday);
                newPreviousEnd = getEndOfDay(lastYearToday);
                setCurrentPeriodShortcut('today');
                setPreviousPeriodShortcut('last_year_today');
                break;
            case 'month_vs_last_month':
                newCurrentStart = getStartOfThisMonthHelper();
                newCurrentEnd = getEndOfThisMonthHelper();
                newPreviousStart = getStartOfLastMonthHelper();
                newPreviousEnd = getEndOfLastMonthHelper();
                setCurrentPeriodShortcut('this_month');
                setPreviousPeriodShortcut('last_month');
                break;
            case 'month_vs_last_year_month':
                newCurrentStart = getStartOfThisMonthHelper();
                newCurrentEnd = getEndOfThisMonthHelper();
                newPreviousStart = getLastYearSameMonthStart(newCurrentStart);
                newPreviousEnd = getLastYearSameMonthEnd(newCurrentEnd);
                setCurrentPeriodShortcut('this_month');
                setPreviousPeriodShortcut('last_year_this_month');
                break;
            case 'quarter_vs_last_quarter':
                newCurrentStart = getStartOfThisQuarterHelper();
                newCurrentEnd = getEndOfThisQuarterHelper();
                newPreviousStart = getStartOfLastQuarterHelper();
                newPreviousEnd = getEndOfLastQuarterHelper();
                setCurrentPeriodShortcut('this_quarter');
                setPreviousPeriodShortcut('last_quarter');
                break;
            case 'quarter_vs_last_year_quarter':
                newCurrentStart = getStartOfThisQuarterHelper();
                newCurrentEnd = getEndOfThisQuarterHelper();
                newPreviousStart = getLastYearSameQuarterStart(newCurrentStart);
                newPreviousEnd = getLastYearSameQuarterEnd(newCurrentEnd);
                setCurrentPeriodShortcut('this_quarter');
                setPreviousPeriodShortcut('last_year_this_quarter');
                break;
            case 'year_vs_last_year':
                newCurrentStart = getStartOfThisYearHelper();
                newCurrentEnd = getEndOfThisYearHelper();
                newPreviousStart = getStartOfLastYearHelper();
                newPreviousEnd = getEndOfLastYearHelper();
                setCurrentPeriodShortcut('this_year');
                setPreviousPeriodShortcut('last_year');
                break;
            default:
                return;
        }

        const todayEndOfDay = getEndOfDay(todayDate);

        if (newCurrentEnd && newCurrentEnd > todayEndOfDay) {
            newCurrentEnd = todayEndOfDay;
        }
        // Cap previous period end date as well, if it's somehow set to future by a shortcut (less common but good for safety)
        if (newPreviousEnd && newPreviousEnd > todayEndOfDay) {
            newPreviousEnd = todayEndOfDay;
        }

        setCurrentPeriodStart(newCurrentStart);
        setCurrentPeriodEnd(newCurrentEnd);
        setPrevPeriodStart(newPreviousStart);
        setFullPrevPeriodEndFromShortcut(newPreviousEnd); // newPreviousEnd is the full (future-capped) P2 end from shortcut

        // actualPrevPeriodEnd will be updated by the useEffect calling recalculateActualPrevPeriodEnd
        // Removed automatic fetchData call
    };

    const handleComparisonMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setComparisonAnchorEl(event.currentTarget);
    };

    const handleComparisonMenuClose = () => {
        setComparisonAnchorEl(null);
    };

    const handleComparisonSelect = (type: string, label: string) => {
        applyComparisonShortcut(type);
        setSelectedComparisonLabel(label);
        handleComparisonMenuClose();
    };

    useEffect(() => {
        const sb = props.userData.selectedBusiness;

        if (!sb || !sb.business_id) { // Ensure sb and sb.business_id exist
            setBusinessIds([]);
            return;
        }

        const selectedBusinessId = parseInt(sb.business_id); // Use parseInt consistently
        const userPermissions = props.userData.userData?.userData?.user_permissions?.map((p: any) => p.codename) || [];
        const isSuperuser = props.userData.userData?.userData?.is_superuser === true;
        const hasSpecificPermission = userPermissions.includes(CAN_VIEW_SALES_BY_COMMODITY_GROUP_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS);
        
        const canActivateAllMayuriMode = isSuperuser || hasSpecificPermission;

        if (showAllMayuriLocations && MAYURI_BUSINESSES.includes(selectedBusinessId) && canActivateAllMayuriMode) {
            setBusinessIds(MAYURI_BUSINESSES);
        } else {
            // Ensure selectedBusinessId is a valid number before putting in array
            setBusinessIds(isNaN(selectedBusinessId) ? [] : [selectedBusinessId]);
        }
    }, [props.userData.selectedBusiness, props.userData.userData, showAllMayuriLocations]);

    // Removed useEffect that was auto-fetching data on date/businessId change.
    // Fetching is now manual via the "Generate Report" button.

    const recalculateActualPrevPeriodEnd = () => {
        const baseP2End = previousPeriodShortcut === 'custom' ? actualPrevPeriodEnd : fullPrevPeriodEndFromShortcut;

        if (limitPeriodRange && currentPeriodStart && currentPeriodEnd && prevPeriodStart) {
            const p1DurationDays = differenceInCalendarDays(currentPeriodEnd, currentPeriodStart) + 1;
            if (p1DurationDays > 0) {
                let limitedP2End = addDays(prevPeriodStart, p1DurationDays - 1);
                setActualPrevPeriodEnd(getEndOfDay(limitedP2End));
            } else {
                // Fallback if P1 duration is not positive
                setActualPrevPeriodEnd(prevPeriodStart ? getEndOfDay(new Date(prevPeriodStart)) : null);
            }
        } else {
            setActualPrevPeriodEnd(baseP2End);
        }
    };

    useEffect(() => {
        recalculateActualPrevPeriodEnd();
    }, [limitPeriodRange, currentPeriodStart, currentPeriodEnd, prevPeriodStart, fullPrevPeriodEndFromShortcut, previousPeriodShortcut]);

    useEffect(() => {
        const p1HasQuarter = currentPeriodShortcut.includes('quarter');
        const p2HasQuarter = previousPeriodShortcut.includes('quarter');

        if (p1HasQuarter || p2HasQuarter) {
            setShowLimitPeriodRangeCheckbox(false);
            setLimitPeriodRange(false);
        } else {
            setShowLimitPeriodRangeCheckbox(true);
        }
    }, [currentPeriodShortcut, previousPeriodShortcut, setShowLimitPeriodRangeCheckbox, setLimitPeriodRange]);


    const chartData = useMemo((): ChartData<'bar'> | null => {
        if (!currentPeriodReportData || !prevPeriodReportData || !allRootGroupNamesSorted || allRootGroupNamesSorted.length === 0) return null;

        // Consolidate all unique businesses from both periods, store id and name, then sort by name
        const businessInfoMap = new Map<number, string>();
        currentPeriodReportData.forEach(item => {
            if (!businessInfoMap.has(item.business_id)) {
                businessInfoMap.set(item.business_id, item.business_name);
            }
        });
        prevPeriodReportData.forEach(item => {
            if (!businessInfoMap.has(item.business_id)) {
                businessInfoMap.set(item.business_id, item.business_name);
            }
        });

        const sortedBusinessesInfo = Array.from(businessInfoMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));

        const chartLabels = sortedBusinessesInfo.map(b => b.name);
        const datasets: any[] = [];

        // First, all datasets for Period 1
        allRootGroupNamesSorted.forEach((groupName, index) => {
            const color = generateColor(index); // Color is based on the commodity group's overall index
            datasets.push({
                label: `${groupName} (Period 1)`,
                data: sortedBusinessesInfo.map(businessInfo => {
                    const businessItem = currentPeriodReportData.find(b => b.business_id === businessInfo.id);
                    if (!businessItem) return 0;
                    const totalBusinessSales = businessItem.data.reduce((sum, rg) => sum + rg.total_gross_item_price, 0);
                    if (totalBusinessSales === 0) return 0;
                    const rootGroupSaleData = businessItem.data.find(rg => rg.root_commodity_group_name === groupName);
                    const groupSales = rootGroupSaleData ? rootGroupSaleData.total_gross_item_price : 0;
                    return (groupSales / totalBusinessSales) * 100;
                }),
                backgroundColor: color,
                stack: 'Period 1',
            });
        });

        // Then, all datasets for Period 2
        allRootGroupNamesSorted.forEach((groupName, index) => {
            const color = generateColor(index); // Use the same color for the same commodity group as in Period 1
            datasets.push({
                label: `${groupName} (Period 2)`,
                data: sortedBusinessesInfo.map(businessInfo => {
                    const businessItem = prevPeriodReportData.find(b => b.business_id === businessInfo.id);
                    if (!businessItem) return 0;
                    const totalBusinessSales = businessItem.data.reduce((sum, rg) => sum + rg.total_gross_item_price, 0);
                    if (totalBusinessSales === 0) return 0;
                    const rootGroupSaleData = businessItem.data.find(rg => rg.root_commodity_group_name === groupName);
                    const groupSales = rootGroupSaleData ? rootGroupSaleData.total_gross_item_price : 0;
                    return (groupSales / totalBusinessSales) * 100;
                }),
                backgroundColor: color,
                stack: 'Period 2',
            });
        });

        return {
            labels: chartLabels,
            datasets,
        };
    }, [currentPeriodReportData, prevPeriodReportData, allRootGroupNamesSorted]);

    const chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y', // Changed to 'y' for horizontal bars
        scales: {
            x: { // X-axis is now the percentage axis
                stacked: true,
                min: 0,
                max: 100,
                ticks: {
                    callback: (value) => `${value}%`,
                },
                title: {
                    display: true,
                    text: 'Percentage of Total Sales',
                },
            },
            y: { // Y-axis is now the business name axis
                stacked: true, // Changed to false for grouped bars (or true if you want stacked horizontal)
            },
        },
        plugins: {
            tooltip: {
                displayColors: true, // Display color boxes
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        // Set up for the current tooltip instance
                        if (tooltipItems.length > 0) {
                            const chart = tooltipItems[0].chart as any; // Add 'as any' to attach custom props
                            chart.renderedGroupsForTooltip = new Set();
                            chart.currentTooltipCommodityData = null; // Clear previous data
                            return tooltipItems[0].label || ''; // Business name as title
                        }
                        return '';
                    },
                    beforeBody: (tooltipItems: any[]) => {
                        if (!tooltipItems.length) return [];

                        const chart = tooltipItems[0].chart as any; // Add 'as any'
                        const businessName = tooltipItems[0].chart.data.labels?.[tooltipItems[0].dataIndex] as string | undefined;

                        if (!businessName) return [];

                        const commodityGroupDataMap = new Map<string, {
                            p1?: { sales: number, percent: number },
                            p2?: { sales: number, percent: number }
                        }>();

                        // Populate commodityGroupDataMap for the current business
                        // This logic iterates over all known commodity groups and finds their P1/P2 data
                        // for the specific 'businessName' derived from the hovered bar.
                        allRootGroupNamesSorted.forEach(groupName => {
                            let p1Data: { sales: number, percent: number } | undefined = undefined;
                            let p2Data: { sales: number, percent: number } | undefined = undefined;

                            // Find P1 data
                            if (currentPeriodReportData) {
                                const businessItem = currentPeriodReportData.find(b => b.business_name === businessName);
                                if (businessItem) {
                                    const rootGroupSaleData = businessItem.data.find(rg => rg.root_commodity_group_name === groupName);
                                    if (rootGroupSaleData) {
                                        const totalBusinessSales = businessItem.data.reduce((sum, rg) => sum + rg.total_gross_item_price, 0);
                                        const percent = totalBusinessSales > 0 ? (rootGroupSaleData.total_gross_item_price / totalBusinessSales) * 100 : 0;
                                        p1Data = { sales: rootGroupSaleData.total_gross_item_price, percent: percent };
                                    }
                                }
                            }
                            // Find P2 data
                            if (prevPeriodReportData) {
                                const businessItem = prevPeriodReportData.find(b => b.business_name === businessName);
                                if (businessItem) {
                                    const rootGroupSaleData = businessItem.data.find(rg => rg.root_commodity_group_name === groupName);
                                    if (rootGroupSaleData) {
                                        const totalBusinessSales = businessItem.data.reduce((sum, rg) => sum + rg.total_gross_item_price, 0);
                                        const percent = totalBusinessSales > 0 ? (rootGroupSaleData.total_gross_item_price / totalBusinessSales) * 100 : 0;
                                        p2Data = { sales: rootGroupSaleData.total_gross_item_price, percent: percent };
                                    }
                                }
                            }

                            if (p1Data || p2Data) {
                                commodityGroupDataMap.set(groupName, { p1: p1Data, p2: p2Data });
                            }
                        });

                        chart.currentTooltipCommodityData = commodityGroupDataMap;
                        return []; // beforeBody doesn't render lines itself
                    },
                    label: (context: any) => { // context is a TooltipItem
                        const chart = context.chart as any; // Add 'as any'
                        const renderedGroups = chart.renderedGroupsForTooltip as Set<string> | undefined;
                        const commodityDataMap = chart.currentTooltipCommodityData as Map<string, {
                            p1?: { sales: number, percent: number },
                            p2?: { sales: number, percent: number }
                        }> | undefined;

                        if (!renderedGroups || !commodityDataMap) return '';

                        const datasetLabel = context.dataset.label || '';
                        let groupName = '';
                        const match = datasetLabel.match(/^(.*)\s\((Period \d)\)$/);
                        if (match) {
                            groupName = match[1].trim();
                        } else {
                            groupName = datasetLabel; // Fallback
                        }

                        if (!groupName || renderedGroups.has(groupName)) {
                            return null; // Suppress line if group already rendered or no group name
                        }

                        if (commodityDataMap.has(groupName)) {
                            const data = commodityDataMap.get(groupName)!;
                            const p1Text = data.p1 ? `P1: ${data.p1.percent.toFixed(2)}%` : 'P1: N/A';
                            const p2Text = data.p2 ? `P2: ${data.p2.percent.toFixed(2)}%` : 'P2: N/A';

                            renderedGroups.add(groupName);
                            return `${groupName}: ${p1Text} | ${p2Text}`;
                        }
                        return ''; // Should not happen if map is built correctly
                    },
                },
                bodySpacing: 4,
            },
            legend: {
                position: 'top' as const,
                display: false, // Disable default Chart.js legend
            },
            title: {
                display: false, // Changed from true
                text: 'Sales Percentage by Root Commodity Group (Period 1 vs Period 2)',
            },
            datalabels: {
                display: false, // Keeping datalabels off as per existing code, adjust if needed for horizontal
                // Example if you wanted to enable and adjust for horizontal:
                // anchor: 'end',
                // align: 'right',
                // formatter: (value, context) => {
                //     return value > 0 ? `${value.toFixed(0)}%` : ''; // Display percentage, hide if 0
                // },
                // font: {
                //     weight: 'bold',
                // },
                // color: '#444'
            },
        },
        interaction: { // To ensure tooltips work well with grouped stacked bars
            mode: 'index',
            intersect: false,
            axis: 'y', // Explicitly set axis for index mode with indexAxis: 'y'
        },
    };

    const handleGenerateReportClick = () => {
        fetchData();
    };

    // Custom Legend Component
    const CustomLegend: React.FC<{
        chart: ChartJS<'bar', any[], string> | null;
        datasetsFromChart: any[]; // Renamed to avoid confusion with component's datasets state
        visible: boolean;
        classes: any;
        groupNamesForLegend: string[];
    }> = ({ chart, datasetsFromChart, visible, classes, groupNamesForLegend }) => {
        const [refreshKey, setRefreshKey] = useState(0); // State to force re-render

        if (!visible || !groupNamesForLegend || groupNamesForLegend.length === 0 || !datasetsFromChart || datasetsFromChart.length === 0) {
            return null;
        }

        const legendItems = groupNamesForLegend.map((groupName, index) => {
            // Find all original dataset indices for this group name
            const associatedDatasetIndices = datasetsFromChart
                .map((ds, i) => ({ label: ds.label, originalIndex: i }))
                .filter(({ label }) => {
                    const match = label.match(/^(.*)\s\(Period \d\)$/);
                    const dsGroupName = match ? match[1].trim() : label;
                    return dsGroupName === groupName;
                })
                .map(({ originalIndex }) => originalIndex);

            return {
                label: groupName, // Display only the group name
                color: generateColor(index), // Use the base color for the legend item
                associatedDatasetIndices,
            };
        });

        const itemsPerColumn = 5;
        const columns: any[][] = [];
        for (let i = 0; i < legendItems.length; i += itemsPerColumn) {
            columns.push(legendItems.slice(i, i + itemsPerColumn));
        }

        const handleLegendItemClick = (item: typeof legendItems[0]) => {
            if (chart && item.associatedDatasetIndices.length > 0) {
                // Toggle based on the visibility of the first associated dataset (e.g., Period 1 for this group)
                const firstDatasetIndex = item.associatedDatasetIndices[0];
                const newVisibility = !chart.isDatasetVisible(firstDatasetIndex);

                item.associatedDatasetIndices.forEach(datasetIndex => {
                    chart.setDatasetVisibility(datasetIndex, newVisibility);
                });
                chart.update();
                setRefreshKey(prevKey => prevKey + 1); // Force re-render of CustomLegend
            }
        };

        return (
            <Box className={classes.legendContainer}>
                {columns.map((columnItems, colIndex) => (
                    <Box key={colIndex} className={classes.legendColumn}>
                        {columnItems.map((item) => {
                            // Determine visibility for styling based on the first associated dataset
                            const isHidden = chart && item.associatedDatasetIndices.length > 0 ? !chart.isDatasetVisible(item.associatedDatasetIndices[0]) : false;
                            return (
                                <Box
                                    key={item.label} // Commodity group name is unique here
                                    className={classes.legendItem}
                                    onClick={() => handleLegendItemClick(item)}
                                    style={{ cursor: 'pointer' /* Opacity handled by Typography now */ }}
                                >
                                    <Box className={classes.legendColorBox} style={{ backgroundColor: item.color }} />
                                    <Typography
                                        className={classes.legendLabel}
                                        style={{
                                            textDecoration: isHidden ? 'line-through' : 'none',
                                            opacity: isHidden ? 0.5 : 1
                                        }}
                                    >
                                        {item.label} {/* This is now just the group name */}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        );
    };

    const canSeeToggle = () => {
        const isBusinessForMultiple = props.userData.selectedBusiness && MAYURI_BUSINESSES.includes(parseInt(props.userData.selectedBusiness.business_id))
        // console.log("userdata", props.userData.userData)
        
        const isSuperUser = props.userData.userData?.userData?.is_superuser === true
        const hasSpecificPermission = props.userData.userData?.userData?.user_permissions?.find((p: any) => p.codename === CAN_VIEW_SALES_BY_COMMODITY_GROUP_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS)
        // console.log(isBusinessForMultiple, isSuperUser, hasSpecificPermission)
        return isBusinessForMultiple && (isSuperUser || hasSpecificPermission)
    }


    return (
        <Paper className={classes.root}>
            <Typography variant="h5" className={classes.title}>
                Sales by Root Commodity Group
            </Typography>

            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container spacing={2} alignItems="center" className={classes.filterButtonContainer}>
                    {/* Left-aligned controls */}
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Button
                                    aria-controls="comparison-menu"
                                    aria-haspopup="true"
                                    variant="outlined"
                                    onClick={handleComparisonMenuOpen}
                                    className={classes.comparisonButton}
                                    endIcon={<ArrowDropDownIcon />}
                                >
                                    {selectedComparisonLabel}
                                </Button>
                                <Menu
                                    id="comparison-menu"
                                    anchorEl={comparisonAnchorEl}
                                    keepMounted
                                    open={Boolean(comparisonAnchorEl)}
                                    onClose={handleComparisonMenuClose}
                                >
                                    <MenuItem onClick={() => handleComparisonSelect('today_vs_yesterday', 'Today vs Yesterday')}>Today vs Yesterday</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('today_vs_last_year_today', 'Today vs Last Year Today')}>Today vs Last Year Today</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('month_vs_last_month', 'This Month vs Last Month')}>This Month vs Last Month</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('month_vs_last_year_month', 'This Month vs Last Year This Month')}>This Month vs Last Year This Month</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('quarter_vs_last_quarter', 'This Quarter vs Last Quarter')}>This Quarter vs Last Quarter</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('quarter_vs_last_year_quarter', 'This Quarter vs Last Year This Quarter')}>This Quarter vs Last Year This Quarter</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('year_vs_last_year', 'This Year vs Last Year')}>This Year vs Last Year</MenuItem>
                                </Menu>
                            </Grid>
                            {showLimitPeriodRangeCheckbox && (
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={limitPeriodRange}
                                                onChange={(e) => setLimitPeriodRange(e.target.checked)}
                                                name="limitPeriodRange"
                                                color="primary"
                                            />
                                        }
                                        label="Limit Period Range"
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                    startIcon={<FilterListIcon />}
                                >
                                    {showAdvancedFilters ? 'Hide Filters' : 'Show Filters'}
                                </Button>
                            </Grid>
                            {canSeeToggle() && (
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showAllMayuriLocations}
                                                onChange={(e) => setShowAllMayuriLocations(e.target.checked)}
                                                name="showAllMayuriLocations"
                                                color="primary"
                                            />
                                        }
                                        label="Across All Businesses"
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowLegend(!showLegend)}
                                    startIcon={showLegend ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                >
                                    {showLegend ? 'Hide Legend' : 'Show Legend'}
                                </Button>
                            </Grid>
                            {/* <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowValuesTable(!showValuesTable)}
                                    startIcon={<TableChartIcon />}
                                >
                                    {showValuesTable ? 'Hide Values' : 'Show Values'}
                                </Button>
                            </Grid> */}
                        </Grid>
                    </Grid>

                    {/* Right-aligned action buttons */}
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={handleExportCsv}
                                    startIcon={<GetAppIcon />}
                                    disabled={exportingCsv || loading || !currentPeriodReportData} // Disable if exporting, loading data, or no data
                                >
                                    {exportingCsv ? <CircularProgress size={20} /> : 'Export CSV'}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleGenerateReportClick}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {showAdvancedFilters && (
                    <form> {/* Removed onSubmit={handleSubmit} */}
                        <Grid container spacing={3} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={3}>
                                <DateShortcutDropdown
                                    key={`current-${currentPeriodShortcut}`}
                                    label="Period 1 Shortcuts"
                                    setStartDate={(newDate) => setCurrentPeriodStart(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    setEndDate={(newDate) => setCurrentPeriodEnd(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    value={currentPeriodShortcut}
                                    onChange={(newShortcut) => {
                                        if (newShortcut !== currentPeriodShortcut) {
                                            setCurrentPeriodShortcut(newShortcut);
                                        }
                                    }}
                                    className={classes.formControl}
                                    shortcutOptions={DEFAULT_SHORTCUT_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DateShortcutDropdown
                                    key={`previous-${previousPeriodShortcut}`}
                                    label="Period 2 Shortcuts"
                                    setStartDate={(newDate) => setPrevPeriodStart(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    setEndDate={(newDate) => {
                                        setFullPrevPeriodEndFromShortcut(currentFullPrevEnd => {
                                            if (newDate === null && currentFullPrevEnd === null) return currentFullPrevEnd;
                                            if (newDate === null || currentFullPrevEnd === null) return newDate;
                                            if (newDate.getTime() !== currentFullPrevEnd.getTime()) {
                                                return newDate;
                                            }
                                            return currentFullPrevEnd;
                                        });
                                     }}
                                    value={previousPeriodShortcut}
                                    onChange={(newShortcut) => {
                                        if (newShortcut !== previousPeriodShortcut) {
                                            setPreviousPeriodShortcut(newShortcut);
                                        }
                                    }}
                                    className={classes.formControl}
                                    shortcutOptions={DEFAULT_SHORTCUT_OPTIONS}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} alignItems="flex-start"> {/* New row for date pickers */}
                            <Grid item xs={12} sm={6} md={3}>
                                {currentPeriodShortcut === 'custom' && (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 1 Start"
                                                value={currentPeriodStart}
                                                onChange={(date) => setCurrentPeriodStart(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 1 End"
                                                value={currentPeriodEnd}
                                                onChange={(date) => setCurrentPeriodEnd(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                {previousPeriodShortcut === 'custom' && (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 2 Start"
                                                value={prevPeriodStart}
                                                onChange={(date) => setPrevPeriodStart(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 2 End"
                                                value={actualPrevPeriodEnd}
                                                onChange={(date) => {
                                                   const newDate = date ? getEndOfDay(new Date(date as any)) : null;
                                                   setActualPrevPeriodEnd(newDate);
                                                   setPreviousPeriodShortcut('custom'); // Mark P2 as custom
                                                   // When P2 end is custom, fullPrevPeriodEndFromShortcut is not strictly needed
                                                   // for recalculateActualPrevPeriodEnd, as it will use actualPrevPeriodEnd.
                                                   // However, to be safe, one might set fullPrevPeriodEndFromShortcut to newDate too.
                                                   // For now, setting previousPeriodShortcut to 'custom' is key.
                                                }}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {/* "Generate Report" button moved to the main button row */}
                    </form>
                )}
            </MuiPickersUtilsProvider>

            {loading && (!currentPeriodReportData || !prevPeriodReportData) ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : error ? (
                <Typography color="error" align="center">{error}</Typography>
            ) : chartData ? (
                <>
                    {/* Button moved to the filter row */}
                    <Grid container spacing={2} className={`${classes.periodDateContainer}`}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle1" component="div" style={{ fontWeight: 'bold' }}>
                                Period 1
                            </Typography>
                            <Typography variant="caption" component="div">
                                {formatDateForDisplay(currentPeriodStart)} - {formatDateForDisplay(currentPeriodEnd)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle1" component="div" style={{ fontWeight: 'bold' }}>
                                Period 2
                            </Typography>
                            <Typography variant="caption" component="div">
                                {formatDateForDisplay(prevPeriodStart)} - {formatDateForDisplay(actualPrevPeriodEnd)}
                            </Typography>
                        </Grid>
                    </Grid>
                    <CustomLegend chart={chartRef.current} datasetsFromChart={chartData?.datasets || []} visible={showLegend} classes={classes} groupNamesForLegend={allRootGroupNamesSorted} />
                    <Box className={classes.chartContainer}>
                        <Bar ref={chartRef} data={chartData} options={chartOptions} />
                    </Box>
                    {showValuesTable && currentPeriodReportData && prevPeriodReportData && (
                        <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Business Name</TableCell>
                                        <TableCell>Root Commodity Group</TableCell>
                                        <TableCell align="right">Period 1 Sales</TableCell>
                                        <TableCell align="right">Period 1 Qty</TableCell>
                                        <TableCell align="right">Period 2 Sales</TableCell>
                                        <TableCell align="right">Period 2 Qty</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.from(new Set([...currentPeriodReportData.map(item => item.business_name), ...prevPeriodReportData.map(item => item.business_name)])).map(businessName => (
                                        Array.from(new Set([
                                            ...currentPeriodReportData.flatMap(item => item.data.map(rg => rg.root_commodity_group_name)),
                                            ...prevPeriodReportData.flatMap(item => item.data.map(rg => rg.root_commodity_group_name))
                                        ])).map(groupName => {
                                            const currentBusinessData = currentPeriodReportData.find(b => b.business_name === businessName);
                                            const currentGroupData = currentBusinessData?.data.find(rg => rg.root_commodity_group_name === groupName);

                                            const prevBusinessData = prevPeriodReportData.find(b => b.business_name === businessName);
                                            const prevGroupData = prevBusinessData?.data.find(rg => rg.root_commodity_group_name === groupName);

                                            return (
                                                <TableRow key={`${businessName}-${groupName}`}>
                                                    <TableCell component="th" scope="row">{businessName}</TableCell>
                                                    <TableCell>{groupName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(currentGroupData?.total_gross_item_price || 0)}</TableCell>
                                                    <TableCell align="right">{currentGroupData?.total_quantity_sold || 0}</TableCell>
                                                    <TableCell align="right">{formatCurrency(prevGroupData?.total_gross_item_price || 0)}</TableCell>
                                                    <TableCell align="right">{prevGroupData?.total_quantity_sold || 0}</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            ) : (
                <Typography align="center">No data available for the selected periods and businesses. Please generate the report.</Typography>
            )}
        </Paper>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

export default connect(mapStateToProps)(SalesByRootCommodityGroupScreen);
